import {
  MinimalPraksisavtaleDto,
  MinimalPraksisbestillingDto,
  MinimalPraksisbesvarelseDto,
  MinimalPraksisstedtilgangDto,
  MinimalStudentPraksisplassDto,
  PraksisstedMedInfoDto,
  PraksisstedMedInfoOgHierarkiDto,
} from '../../api/generated/admin-generated-api';
import { RawNodeDatum } from 'react-d3-tree/lib/types/types/common';
import * as css from './praksisstedsok.module.scss';
import classNames from 'classnames';
import { Reducer, useMemo } from 'react';
import {
  NodeCircleTypes,
  PraksisstedMedInfoDtoArrays,
  Search,
  SearchPayload,
  SearchWithResults,
} from './types';
import { GenericActionWithPayload } from '../../util';

const mapToRawNodeDatumOvernoder = (
  overnoder: PraksisstedMedInfoDto[],
  selv: RawNodeDatum,
): RawNodeDatum =>
  [...overnoder].reverse().reduce(
    (acc: RawNodeDatum, node: PraksisstedMedInfoDto) => ({
      name: node.navn,
      children: Object.keys(acc).length > 0 ? [acc] : [],
      attributes: { id: node.id },
    }),
    selv,
  );

const mapToRawNodeDatumUndernoder = (
  undernoder: PraksisstedMedInfoDto[],
): RawNodeDatum[] =>
  undernoder.reduce(
    (acc: RawNodeDatum[], undernode: PraksisstedMedInfoDto) =>
      acc.concat({
        name: undernode.navn,
        children: mapToRawNodeDatumUndernoder(undernode.undernoder),
        attributes: { id: undernode.id },
      }),
    [],
  );

const getInnerNodeCircleClassName = (
  type: NodeCircleTypes,
): string[] | string => {
  switch (type) {
    case NodeCircleTypes.MainRelevant:
      return [css.mainCircle, css.relevantCircle];
    case NodeCircleTypes.Relevant:
      return css.relevantCircle;
    case NodeCircleTypes.MainIrrelevant:
      return [css.mainCircle, css.irrelevantCircle];
    case NodeCircleTypes.Irrelevant:
      return css.irrelevantCircle;
    case NodeCircleTypes.Overnode:
      return css.overnodeCircle;
  }
};

export enum SearchActionType {
  SET_OPEN,
  SET_RESULTS,
  SET_CHOSEN_STED,
  SET_SEARCH,
}

type SearchAction = GenericActionWithPayload<SearchActionType, SearchPayload>;

export const searchReducer: Reducer<SearchWithResults, SearchAction> = (
  state,
  action,
) => {
  if (!action.payload) return state;

  const { search, searchResults, searchResultsModalOpen, chosenPlace } =
    action.payload;

  const includeDeletedHasChanged =
    search?.includeDeleted !== undefined &&
    state.search.includeDeleted !== search.includeDeleted;

  switch (action.type) {
    case SearchActionType.SET_OPEN:
      return {
        ...state,
        searchResultsModalOpen: searchResultsModalOpen!!,
        chosenPlace: includeDeletedHasChanged ? undefined : state.chosenPlace,
      };
    case SearchActionType.SET_RESULTS:
      return {
        ...state,
        search: search as Search,
        searchResults: searchResults || [],
        searchResultsModalOpen: true,
      };
    case SearchActionType.SET_CHOSEN_STED:
      return {
        ...state,
        chosenPlace,
        searchResultsModalOpen: false,
      };
    case SearchActionType.SET_SEARCH:
      return {
        ...state,
        search: search as Search,
      };
  }

  return state;
};

export const mapPraksisstedToRawNodeDatum = (
  praksissted: PraksisstedMedInfoOgHierarkiDto,
): RawNodeDatum => {
  const { selv, overnoder, undernoder } = praksissted;
  const { navn, id } = selv;

  const children = mapToRawNodeDatumUndernoder(undernoder);

  return mapToRawNodeDatumOvernoder(overnoder, {
    name: navn,
    children,
    attributes: { id },
  });
};

export const getNodeCircleClassName = (type: NodeCircleTypes): string =>
  classNames([css.circle].concat(getInnerNodeCircleClassName(type)));

export const getNodeCircleType = (
  isOvernode: boolean,
  isMainNode: boolean,
  isRelevant: boolean,
): NodeCircleTypes => {
  if (isOvernode) return NodeCircleTypes.Overnode;

  if (isMainNode) {
    return isRelevant
      ? NodeCircleTypes.MainRelevant
      : NodeCircleTypes.MainIrrelevant;
  }

  return isRelevant ? NodeCircleTypes.Relevant : NodeCircleTypes.Irrelevant;
};

export const hasAnyRelevantChildFlattenedUndernoder = (
  flattenedUndernoder: PraksisstedMedInfoDto[],
) => flattenedUndernoder.some((undernode) => undernode.relevant);

export const flattenUndernoder = (
  undernoder: PraksisstedMedInfoDto[],
): PraksisstedMedInfoDto[] =>
  undernoder.reduce(
    (acc: PraksisstedMedInfoDto[], undernode: PraksisstedMedInfoDto) =>
      acc.concat([undernode, ...flattenUndernoder(undernode.undernoder)]),
    [],
  );

export const hasAnyRelevantChild = (
  praksissted: PraksisstedMedInfoDto,
): boolean =>
  flattenUndernoder(praksissted.undernoder).some(
    (undernode) => undernode.relevant,
  );

export const getAccArraysForUndernoder = (
  flattenedUndernoder: PraksisstedMedInfoDtoArrays[],
): PraksisstedMedInfoDtoArrays => {
  const praksisbestillinger: MinimalPraksisbestillingDto[] = [];
  const praksisbesvarelser: MinimalPraksisbesvarelseDto[] = [];
  const praksisavtaler: MinimalPraksisavtaleDto[] = [];
  const praksisstedtilganger: MinimalPraksisstedtilgangDto[] = [];
  const studentPraksisplasser: MinimalStudentPraksisplassDto[] = [];

  flattenedUndernoder.forEach((praksissted) => {
    const {
      praksisavtaler: avtaler,
      praksisbestillinger: bestillinger,
      praksisbesvarelser: besvarelser,
      studentPraksisplasser: sp,
      praksisstedtilganger: pt,
    } = praksissted;

    const currentAvtaleIds = praksisavtaler.map((avtale) => avtale.avtaleId);
    const currentBestillingIds = praksisbestillinger.map(
      (bestilling) => bestilling.bestillingId,
    );
    const currentBesvarelseIds = praksisbesvarelser.map(
      (besvarelse) => besvarelse.besvarelseId,
    );
    const currentStudentpraksisplassIds = studentPraksisplasser.map(
      (studentPraksisplass) => studentPraksisplass.studentPraksisplassId,
    );
    const currentTilgangPairs = praksisstedtilganger.map(
      (praksisstedtilgang) => ({
        praksisstedId: praksisstedtilgang.praksisstedId,
        brukerId: praksisstedtilgang.brukerId,
      }),
    );

    praksisavtaler.push(
      ...avtaler.filter(
        (avtale) => !currentAvtaleIds.includes(avtale.avtaleId),
      ),
    );
    praksisbestillinger.push(
      ...bestillinger.filter(
        (bestilling) => !currentBestillingIds.includes(bestilling.bestillingId),
      ),
    );
    praksisbesvarelser.push(
      ...besvarelser.filter(
        (besvarelse) => !currentBesvarelseIds.includes(besvarelse.besvarelseId),
      ),
    );
    studentPraksisplasser.push(
      ...sp.filter(
        (studentPraksisplass) =>
          !currentStudentpraksisplassIds.includes(
            studentPraksisplass.studentPraksisplassId,
          ),
      ),
    );
    praksisstedtilganger.push(
      ...pt.filter(
        (tilgang) =>
          !currentTilgangPairs.includes({
            praksisstedId: tilgang.praksisstedId,
            brukerId: tilgang.brukerId,
          }),
      ),
    );
  });

  return {
    praksisbestillinger: [...new Set(praksisbestillinger)],
    praksisavtaler: [...new Set(praksisavtaler)],
    praksisbesvarelser: [...new Set(praksisbesvarelser)],
    praksisstedtilganger: [...new Set(praksisstedtilganger)],
    studentPraksisplasser: [...new Set(studentPraksisplasser)],
  };
};

// HOOKS
export const useShowOnSelfOrChildren = (
  onSelf: unknown[],
  onChildren: unknown[],
): { onSelf: boolean; onChildren: boolean } =>
  useMemo(
    () => ({ onSelf: onSelf.length > 0, onChildren: onChildren.length > 0 }),
    [onSelf, onChildren],
  );
